import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type { Workspace } from "../../types";

export interface TourStep {
  tag: string;
  title: string;
  body: JSX.Element;
}

/* Eight steps that walk the actual workflow — Library → Simulation → three-pane
   runtime → Approvals → Memo → Eval — using the Lumen Series B example as the
   running thread. */
export const TOUR_STEPS: TourStep[] = [
  {
    tag: "01 · What this is",
    title: "A measurement layer for AI in your inbox",
    body: (
      <>
        <p>
          Triage is the <strong>calibration environment</strong> for an AI system that reads,
          sorts, and acts on the deal email your firm already handles by hand. It is{" "}
          <strong>production minus the live model</strong>, plus a <strong>ground truth</strong> —
          so you can see exactly how the system would behave on a real corpus before it ever
          touches a live mailbox.
        </p>
        <p className="tournote">
          <strong>Who this is for:</strong> partners and innovation leads evaluating whether this
          can carry real financings and fund formations work — not engineers tuning prompts.
        </p>
        <p>
          The next seven steps walk the system using the{" "}
          <strong>Lumen Robotics — Series B</strong> replay: a 15-email thread, $45M raise, a
          $185M-vs-$180M cap-table conflict, and the lawyer-in-the-loop that resolves it.
        </p>
      </>
    ),
  },
  {
    tag: "02 · The taxonomy",
    title: "Communications → workstreams → actions",
    body: (
      <>
        <p>Three stages, one continuous flow:</p>
        <ul>
          <li>
            <strong>Communications.</strong> Anything that arrives — email today, voice and chat
            next.
          </li>
          <li>
            <strong>Workstreams.</strong> The matter (and other types) each message gets filed
            under. Sorting happens here.
          </li>
          <li>
            <strong>Actions.</strong> The Workstream Agents — closing checklist, signature
            tracker, deal-memo fidelity, funds-flow, state-of-play — each owning a slice of the
            recurring deal work.
          </li>
        </ul>
        <p>
          Between stages 1 and 2 is the <strong>spine</strong>:{" "}
          <em>catalog → clean → extract → append → tag</em>. The spine never acts on the outside
          world; the lawyer stays in the loop for every consequential output.
        </p>
      </>
    ),
  },
  {
    tag: "03 · Library",
    title: "The reusable assets behind every run",
    body: (
      <>
        <p>The Library holds four kinds of asset, all versioned:</p>
        <ul>
          <li>
            <strong>Email corpus</strong> — the threads you replay against (yours, redacted; or
            worked examples).
          </li>
          <li>
            <strong>Workstream bundles</strong> — the matter, its working group, the deal memo.
          </li>
          <li>
            <strong>Check registry</strong> — every detector, its prompt, its threshold τ, and the
            model class it runs on.
          </li>
          <li>
            <strong>Eval sets</strong> — the ground-truth answer key for a corpus.
          </li>
        </ul>
        <p>
          Variants live as duplicates, so tuning a single τ never disturbs an earlier run. Every
          simulation pins a specific version of each asset — its outputs are reproducible.
        </p>
      </>
    ),
  },
  {
    tag: "04 · A simulation",
    title: "Name it, pick the pinned inputs, run it",
    body: (
      <>
        <p>
          A simulation is a <strong>named workspace</strong>: a corpus + a workstream bundle + a
          check-set version + an optional eval set, all pinned. Click <strong>New simulation</strong>{" "}
          to assemble one.
        </p>
        <p>
          Then activate the workspace and step the feed forward. The three-pane runtime that opens
          is the same surface a working lawyer would see in production — only here, every email is
          a recording you've already cleared.
        </p>
      </>
    ),
  },
  {
    tag: "05 · The three-pane runtime",
    title: "Feed · Spine · Lawyer",
    body: (
      <>
        <p>
          Left: the <strong>raw email feed</strong> as it arrived. Centre: the{" "}
          <strong>spine viewer</strong> — what the system extracted from each message (cleaned
          delta, candidate deal-memo facts, tags, every check that ran with its threshold and
          confidence). Right: the <strong>lawyer's panel</strong> — approvals, the live deal memo,
          the actions taken by each Workstream Agent, the checks-toggle calibration surface, and
          the run's metered cost.
        </p>
        <p>
          The spine viewer is where you can interrogate any decision: open a card, expand a check,
          see the prompt that ran and the output it produced.
        </p>
      </>
    ),
  },
  {
    tag: "06 · The lawyer-in-the-loop",
    title: "Approvals are the unit of control",
    body: (
      <>
        <p>
          Nothing consequential happens without the lawyer's confirmation. The{" "}
          <strong>Approvals</strong> tab queues:
        </p>
        <ul>
          <li>
            <strong>Deal-memo candidates</strong> — facts to lock onto the memo (round size,
            pre-money, preference).
          </li>
          <li>
            <strong>Fact conflicts</strong> — e.g. the CFO cap table says $185M pre, the signed
            term sheet says $180M. Pick which governs; the memo updates and prior emails are
            reprocessed against the corrected context.
          </li>
          <li>
            <strong>New participants</strong> — anyone outside the confirmed working group; role
            verification required.
          </li>
          <li>
            <strong>Privilege flags</strong> — sensitive content stays restricted to participants
            until widened.
          </li>
          <li>
            <strong>Phase advances</strong> — Term Sheet Signed → Documentation, Signing →
            Pre-Closing, etc.
          </li>
        </ul>
      </>
    ),
  },
  {
    tag: "07 · The deal memo",
    title: "One clean, current, change-tracked record",
    body: (
      <>
        <p>
          The deal memo is the single source of truth the lawyer owns. The system{" "}
          <strong>never decides what a fact is</strong>; it surfaces candidates, you confirm them.
          Updates keep history (round $40M → $45M). Contradictions become conflicts, not silent
          overwrites.
        </p>
        <p>
          In the Lumen replay, watch the memo grow as approvals land — by closing, it reflects the
          executed deal, with every value traceable to the email that established it.
        </p>
      </>
    ),
  },
  {
    tag: "08 · Evaluation",
    title: "Two numbers, every run",
    body: (
      <>
        <p>If you attach an eval set, every run gets scored against the answer key:</p>
        <div className="keymetric">
          <strong>Cataloging accuracy</strong> — did each email land in the expected matter, or in
          Unassociated when it should have?
        </div>
        <div className="keymetric">
          <strong>Detection recall</strong> — did the planted issues (conflicts, multi-match,
          privilege, phase changes, new participants, noise) get caught?
        </div>
        <p>
          Pair those with the <strong>Usage</strong> tab — metered cost, plus dollars per memo
          fact surfaced and per conflict raised — and you have a calibration story you can take to
          a credentialing committee, not just a demo.
        </p>
        <p className="tournote">
          <strong>Ready to look around?</strong> Open <em>Simulations</em> to step through the
          Lumen replay, or <em>Library</em> to see how the assets compose. The Tour stays available
          in the header.
        </p>
      </>
    ),
  },
];

export interface TourProps {
  onClose: () => void;
  goSim: () => void;
  goLibrary: () => void;
  workspaces: Workspace[];
}

/* Modal product tour — keyboard-navigable (Esc / ArrowRight / ArrowLeft). */
export function Tour({ onClose, goSim, workspaces }: TourProps): JSX.Element {
  const [i, setI] = useState<number>(0);
  const s = TOUR_STEPS[i];
  const last = i === TOUR_STEPS.length - 1;
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" && !last) setI((x) => x + 1);
      else if (e.key === "ArrowLeft" && i > 0) setI((x) => x - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [i, last, onClose]);
  const completed = (workspaces || []).filter((w) => w.status === "completed");
  return (
    <div
      className="tourbackdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-title"
      onClick={onClose}
    >
      <div className="tourcard" onClick={(e) => e.stopPropagation()}>
        <div className="tourhd">
          <span className="tourtag">{s.tag}</span>
          <span className="spacer" />
          <button className="icon-btn" aria-label="Close tour" onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="tourbody">
          <h3 id="tour-title">{s.title}</h3>
          {s.body}
        </div>
        <div className="tourft">
          <div className="tourdots" aria-hidden="true">
            {TOUR_STEPS.map((_, k) => (
              <span key={k} className={k === i ? "on" : ""} />
            ))}
          </div>
          <span className="mono tert" style={{ fontSize: 11, marginLeft: 8 }}>
            Step {i + 1} of {TOUR_STEPS.length}
          </span>
          <span className="spacer" />
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            Skip
          </button>
          {i > 0 && (
            <button className="btn btn-ghost btn-sm" onClick={() => setI(i - 1)}>
              <ChevronLeft size={13} /> Back
            </button>
          )}
          {!last && (
            <button className="btn btn-primary btn-sm" onClick={() => setI(i + 1)}>
              Next <ChevronRight size={13} />
            </button>
          )}
          {last && (
            <>
              {completed.length > 0 && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    onClose();
                    goSim();
                  }}
                >
                  Open Simulations
                </button>
              )}
              <button className="btn btn-primary btn-sm" onClick={onClose}>
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
